import React, { useState, useEffect, useRef } from 'react';
import { cleanApiBaseUrl, rag, records, chat, askStream } from '../lib/api';
import * as speech from '../lib/speech';
import * as dictation from '../lib/dictation';
import toast from 'react-hot-toast';
import { Send, Sparkles, FileText, Database, RefreshCw, CheckCircle, AlertCircle, Plus, MessageSquare, Trash2, Clock, Volume2, Square, Mic, MicOff, Loader2, Pencil } from 'lucide-react';
import { format } from 'date-fns';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const EXAMPLE_QUESTIONS = [
  "What did the council decide about the parks budget in 2022?",
  "Which ordinances relate to zoning changes in downtown?",
  "What were the key points from the city attorney memo on contract review?",
  "Summarize the most recent meeting minutes",
  "What resolutions were passed regarding infrastructure?",
];

// How many sources are shown before collapsing.
//
// Retrieval hands the model 10 chunks from up to 10 different records, and runs
// of 9 and 6 sources were observed — most at 43-55%, contributing nothing to the
// answer. The list read as noise and diluted the citations that mattered
// (Finding #104). The full list is still rendered, just folded: the model cites
// by POSITION ([Source 3]), so dropping entries outright would break any
// citation pointing past the cut.
const SOURCES_SHOWN = 3;

function SourceList({ sources, apiBase }) {
  const [expanded, setExpanded] = React.useState(false);
  const hidden = sources.length - SOURCES_SHOWN;
  const visible = expanded ? sources : sources.slice(0, SOURCES_SHOWN);

  return (
    <div className="mt-2">
      <div className="text-xs text-gray-400 mb-1.5 ml-1">
        {sources.length === 1 ? '1 source' : `${sources.length} sources`}
      </div>
      <div className="grid grid-cols-1 gap-1.5">
        {visible.map((s, j) => <SourceCard key={j} source={s} apiBase={apiBase} />)}
      </div>
      {hidden > 0 && (
        <button onClick={() => setExpanded(v => !v)}
          className="mt-1.5 ml-1 text-xs text-blue-600 hover:text-blue-700 transition-colors">
          {expanded ? 'Show fewer' : `Show ${hidden} more`}
        </button>
      )}
    </div>
  );
}

function SourceCard({ source, apiBase }) {
  return (
    <a href={`${apiBase}/uploads/${source.file_path}`} target="_blank" rel="noopener noreferrer"
      className="flex items-start gap-2.5 p-3 bg-white rounded-lg border border-gray-100 hover:border-blue-200 hover:bg-blue-50/30 transition-all group">
      <FileText size={14} className="text-blue-400 mt-0.5 flex-shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-xs font-medium text-gray-800 truncate group-hover:text-blue-700">{source.title}</div>
        <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
          {source.document_type && <span className="text-xs text-gray-400">{source.document_type}</span>}
          {source.department && <span className="text-xs text-gray-300">·</span>}
          {source.department && <span className="text-xs text-gray-400">{source.department}</span>}
        </div>
      </div>
      <span className="text-xs text-teal-600 font-medium flex-shrink-0 bg-teal-50 px-1.5 py-0.5 rounded-full">{source.similarity}%</span>
    </a>
  );
}

export default function AskPage() {
  const [question, setQuestion] = useState('');
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState(null);
  const [indexing, setIndexing] = useState(false);
  const [cityName, setCityName] = useState('');
  const [exampleQuestions, setExampleQuestions] = useState(EXAMPLE_QUESTIONS);
  // Persisted so a reload resumes the same thread instead of silently starting a
  // new one — a chat that forgets on refresh reads as a chat with no memory.
  const [conversationId, setConversationId] = useState(() => {
    try { return localStorage.getItem('cv_conversation_id') || null; } catch { return null; }
  });
  const [threads, setThreads] = useState([]);
  const [showThreads, setShowThreads] = useState(false);
  // Thread rename (todo #185). The endpoint, its audit row and chat.rename() all
  // existed already and nothing called them, so a title stayed exactly as
  // titleFrom() derived it from the first question — which on the medical tenant
  // makes the thread list a column of uneditable PHI fragments.
  const [renamingId, setRenamingId] = useState(null);
  const [renameText, setRenameText] = useState('');
  // Read-aloud. Which message is speaking (by index) — only one at a time, and
  // the button must never be left stuck in the speaking state, so every exit path
  // clears it.
  const [speakingIndex, setSpeakingIndex] = useState(null);
  // Split deliberately into "does this environment allow it" and "can this
  // browser do it", each set independently. They were previously computed on one
  // line inside the /api/config .then(), where a throw from the speech check was
  // swallowed by that fetch's .catch() and the button silently never appeared —
  // with nothing anywhere to say why.
  const [voiceAllowed, setVoiceAllowed] = useState(true);
  const [voiceSupported, setVoiceSupported] = useState(false);
  const voiceEnabled = voiceAllowed && voiceSupported;
  // Dictation, split the same way and for the same reason as read-aloud above:
  // the environment's opinion, the browser's capability, and — new here — whether
  // the on-device LANGUAGE PACK is actually usable, which is a third thing that
  // can independently be false. Edge has the API and the SODA component and still
  // reports 'unavailable' (Finding #114), so support and availability must not be
  // collapsed into one flag.
  const [dictationAllowed, setDictationAllowed] = useState(true);
  const [dictationReady, setDictationReady] = useState('unavailable');
  const [dictating, setDictating] = useState(false);
  const [preparingMic, setPreparingMic] = useState(false);
  const [micBlocked, setMicBlocked] = useState(false);
  const dictationOffered = dictationAllowed && dictationReady !== 'unavailable';
  const bottomRef = useRef(null);
  const inputRef = useRef(null);
  // What was already typed when the microphone opened. Dictation APPENDS to it
  // rather than replacing the box, so a half-composed question survives someone
  // reaching for the mic to finish it.
  const dictationBaseRef = useRef('');
  // Read by the keyboard handler, which is registered once and must not close
  // over a stale `question`. A ref avoids re-binding window listeners on every
  // keystroke.
  const questionRef = useRef('');
  // Distinguishes push-to-talk from the click toggle: releasing the key must only
  // stop dictation that the key started.
  const pushToTalkRef = useRef(false);
  // Lets a click cancel a language-pack wait. Without it the only way out of a
  // long download was to reload the page.
  const prepareCancelRef = useRef(false);
  const apiBase = cleanApiBaseUrl();

  useEffect(() => {
    rag.status().then(r => setStatus(r.data)).catch(() => {});
    inputRef.current?.focus();

    // Fetch city config for branding
    const apiBaseUrl = cleanApiBaseUrl();
    fetch(`${apiBaseUrl}/api/config`)
      .then(r => r.json())
      .then(data => {
        if (data.city_name) setCityName(data.city_name);
        if (Array.isArray(data.ask_examples) && data.ask_examples.length) setExampleQuestions(data.ask_examples);
        // Only the environment's opinion. Browser support is checked separately,
        // so a failure in one cannot take out the other.
        setVoiceAllowed(data.voice_read_aloud !== false);
        // Read optimistically: absent means allowed, so no backend change is
        // needed today, and an environment that later wants dictation off gets a
        // switch without one either.
        setDictationAllowed(data.voice_dictation !== false);
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Browser capability, in its own effect and its own try/catch, so it is
  // diagnosable rather than silent. The console line is the whole point: if the
  // Listen button is missing, this says which half is false.
  useEffect(() => {
    let supported = false;
    try {
      supported = typeof speech.isSupported === 'function' && speech.isSupported();
    } catch (e) {
      console.warn('Read-aloud unavailable — speech module failed to load:', e.message);
    }
    setVoiceSupported(supported);
    // Logged UNCONDITIONALLY. Logging only the failure made an empty console
    // ambiguous — it could mean "not supported and I told you" or "working
    // fine", which is exactly the question being asked.
    console.log('[CogniVault] read-aloud support:', supported,
      '| speechSynthesis present:', typeof window !== 'undefined' && 'speechSynthesis' in window,
      '| voices:', (() => { try { return window.speechSynthesis.getVoices().length; } catch { return 'n/a'; } })());
  }, []);

  // Leaving the page mid-sentence must not leave the browser talking.
  useEffect(() => () => speech.stop(), []);

  // Leaving the page must not leave the MICROPHONE open either, which is a
  // stronger obligation than the one above.
  useEffect(() => () => dictation.stop(), []);

  useEffect(() => { questionRef.current = question; }, [question]);

  // On-device capability, asked once and logged unconditionally for the same
  // reason the read-aloud probe is: when the mic button is missing, an empty
  // console cannot distinguish "unsupported and reported" from "working".
  useEffect(() => {
    let cancelled = false;
    (async () => {
      let state = 'unavailable';
      try {
        state = dictation.isSupported() ? await dictation.availability() : 'unavailable';
      } catch (e) {
        console.warn('Dictation unavailable — capability check failed:', e.message);
      }
      if (!cancelled) setDictationReady(state);
      console.log('[CogniVault] dictation on-device:', state,
        '| API present:', (() => { try { return dictation.isSupported(); } catch { return 'n/a'; } })(),
        '| note: Arabic is not offered by this route at all — see Finding #115');

      // 'downloading' means the browser is fetching the pack right now, on its own
      // schedule and with no event to announce completion. Waiting it out HERE is
      // what stops the first click landing on a pack that is not ready — which is
      // exactly what happened on 2026-08-24. Bounded, and cancelled on unmount.
      if (state === 'downloading' && !cancelled) {
        const settled = await dictation.settle(undefined, {
          onState: s => { if (!cancelled) setDictationReady(s); },
          isCancelled: () => cancelled
        });
        console.log('[CogniVault] dictation pack settled to:', settled);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const toggleSpeak = (index, text) => {
    if (speakingIndex === index) {
      speech.stop();
      setSpeakingIndex(null);
      return;
    }
    speech.stop();
    const started = speech.speak(text, { onEnd: () => setSpeakingIndex(null) });
    setSpeakingIndex(started ? index : null);
  };

  // The transcript is composed as base + dictated so the two never fight: the
  // module hands back the whole utterance each time, and the base is fixed for the
  // life of the session.
  const applyTranscript = (text) => {
    const base = dictationBaseRef.current;
    setQuestion(base ? `${base.trim()} ${text}` : text);
  };

  const beginDictation = async () => {
    if (dictating || preparingMic) return;

    // The pack may need fetching first. This runs inside a click, which is what
    // install() requires — it cannot be done from an effect on mount.
    //
    // prepare() returns a STATE, not a boolean, because the outcomes are not
    // interchangeable and the user deserves to be told which one happened rather
    // than watching a spinner.
    if (dictationReady !== 'available') {
      prepareCancelRef.current = false;
      setPreparingMic(true);
      const state = await dictation.prepare(undefined, {
        onState: s => setDictationReady(s),
        isCancelled: () => prepareCancelRef.current
      });
      setPreparingMic(false);
      setDictationReady(state);

      if (state !== 'available') {
        if (prepareCancelRef.current) toast('Dictation setup cancelled');
        else if (state === 'downloading') toast.error('Still downloading the voice pack — try again in a moment');
        else if (state === 'downloadable') toast.error('The browser did not install the voice pack — try again');
        else toast.error('On-device dictation is not available in this browser');
        return;
      }
    }

    // An open microphone must not be fed the app's own answer being read aloud.
    speech.stop();
    setSpeakingIndex(null);
    setMicBlocked(false);
    dictationBaseRef.current = questionRef.current;

    const started = dictation.start({
      onInterim: applyTranscript,
      onFinal: applyTranscript,
      onError: (code) => {
        // 'not-allowed' and 'service-not-allowed' are the permission refusals;
        // they get a persistent button state rather than a toast that vanishes,
        // because the fix is in browser settings and the user needs to still be
        // able to see that something is wrong.
        if (code === 'not-allowed' || code === 'service-not-allowed') setMicBlocked(true);
        else if (code === 'language-not-supported') {
          // The measured refusal from Finding #114: on-device was not actually
          // usable. NOT a cloud fallback — the browser declined, which is the
          // behaviour this feature depends on.
          setDictationReady('unavailable');
          toast.error('On-device dictation is not available in this browser');
        } else toast.error('Dictation stopped');
      },
      onEnd: () => {
        setDictating(false);
        pushToTalkRef.current = false;
        inputRef.current?.focus();
      }
    });

    setDictating(started);
    if (!started) toast.error('Could not start dictation');
  };

  const toggleDictation = () => {
    // A click DURING preparation cancels the wait. Previously the button was
    // disabled while preparing, so a slow pack download could only be escaped by
    // reloading the page.
    if (preparingMic) {
      prepareCancelRef.current = true;
      return;
    }
    if (dictating) {
      pushToTalkRef.current = false;
      dictation.stop();
      return;
    }
    beginDictation();
  };

  const rememberConversation = (id) => {
    setConversationId(id);
    try {
      if (id) localStorage.setItem('cv_conversation_id', id);
      else localStorage.removeItem('cv_conversation_id');
    } catch { /* private browsing — the thread just will not resume */ }
  };

  const loadThreads = async () => {
    try {
      const { data } = await chat.listConversations();
      setThreads(data.conversations || []);
    } catch {
      // Migration 011 may not be applied — no threads is a valid state, not an
      // error worth showing the user.
      setThreads([]);
    }
  };

  // Resume the stored thread on mount. A conversation_id that is not this user's
  // returns no messages, so a stale value simply starts a fresh thread.
  useEffect(() => {
    loadThreads();
    if (!conversationId) return;
    chat.getConversation(conversationId)
      .then(({ data }) => {
        const restored = (data.messages || []).map(m => ({
          role: m.role,
          text: m.content,
          sources: m.sources || [],
          answeredBy: m.answered_by
        }));
        if (restored.length) setMessages(restored);
        else rememberConversation(null);
      })
      .catch(() => rememberConversation(null));
    // Intentionally runs once on mount only. This project's ESLint config does not
    // include the react-hooks plugin, so a disable comment for exhaustive-deps is
    // itself an error here — hence a plain comment.
  }, []);

  // Push-to-talk: hold Ctrl+Space, release to stop. Registered once and driven by
  // refs, so it does not re-bind on every keystroke.
  //
  // Ctrl+Space rather than the spacebar alone, which would type. e.repeat is
  // guarded because a held key autorepeats and would otherwise restart the
  // session dozens of times a second.
  useEffect(() => {
    if (!dictationOffered) return undefined;

    const onKeyDown = (e) => {
      if (!e.ctrlKey || e.code !== 'Space' || e.repeat) return;
      e.preventDefault();
      if (dictation.isActive()) return;
      pushToTalkRef.current = true;
      beginDictation();
    };

    // Releasing EITHER half ends it. Requiring both to be released would leave
    // the mic open when someone lets go of Ctrl first, which is the common way to
    // release a chord.
    const onKeyUp = (e) => {
      if (e.code !== 'Space' && e.key !== 'Control') return;
      if (!pushToTalkRef.current) return;
      pushToTalkRef.current = false;
      dictation.stop();
    };

    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('keyup', onKeyUp);
    };
    // Re-bound only when the feature is offered or the readiness state changes;
    // everything else the handlers need is read from refs. (This project's ESLint
    // config has no react-hooks plugin, so an exhaustive-deps disable comment is
    // itself an error — hence this note instead.)
  }, [dictationOffered, dictationReady, dictating, preparingMic]);

  const newThread = () => {
    speech.stop();
    dictation.stop();
    setSpeakingIndex(null);
    rememberConversation(null);
    setMessages([]);
    setShowThreads(false);
    inputRef.current?.focus();
  };

  const openThread = async (id) => {
    speech.stop();
    dictation.stop();
    setSpeakingIndex(null);
    setShowThreads(false);
    try {
      const { data } = await chat.getConversation(id);
      setMessages((data.messages || []).map(m => ({
        role: m.role,
        text: m.content,
        sources: m.sources || [],
        answeredBy: m.answered_by
      })));
      rememberConversation(id);
    } catch {
      toast.error('Could not open that conversation');
    }
  };

  const startRename = (thread, e) => {
    e.stopPropagation();
    setRenamingId(thread.id);
    setRenameText(thread.title || '');
  };

  const commitRename = async (id) => {
    const title = renameText.trim();
    setRenamingId(null);
    // An empty title is a cancel, not a request to blank the thread: the server
    // rejects it with a 400 anyway, and a toast for a slip the user obviously did
    // not mean is noise.
    if (!title) return;
    try {
      await chat.rename(id, title);
      loadThreads();
    } catch {
      toast.error('Could not rename that conversation');
    }
  };

  const deleteThread = async (id, e) => {
    e.stopPropagation();
    try {
      await chat.remove(id);
      if (id === conversationId) newThread();
      loadThreads();
    } catch {
      toast.error('Could not delete that conversation');
    }
  };

  const ask = async (q) => {
    const question_text = q || question.trim();
    if (!question_text) return;
    setQuestion('');
    setLoading(true);

    setMessages(m => [...m, { role: 'user', text: question_text }]);

    try {
      // No top_k: let the SERVER decide. It was hardcoded to 5 here, which
      // silently overrode the server default and made a whole retrieval test
      // measure nothing (Finding #98). The tuning lives in one place now.
      // Placeholder the tokens stream into. Added before the request so the
      // bubble appears immediately rather than after the first token.
      let index = -1;
      setMessages(m => { index = m.length; return [...m, { role: 'assistant', text: '', streaming: true }]; });

      // Tokens arrive individually and a setState per token would re-render
      // hundreds of times. Buffered and flushed on a timer instead: still reads
      // as typing, at a fraction of the work.
      let pending = null;
      const flush = () => {
        if (pending === null) return;
        const text = pending;
        pending = null;
        setMessages(m => m.map((msg, j) => (j === index ? { ...msg, text } : msg)));
      };
      const ticker = setInterval(flush, 60);

      try {
        await askStream({
          question: question_text,
          conversation_id: conversationId,
          onMeta: (meta) => {
            // The server creates the thread on the first turn and returns its id.
            if (meta.conversation_id && meta.conversation_id !== conversationId) {
              rememberConversation(meta.conversation_id);
              loadThreads();
            }
            // Sources are known the moment retrieval finishes, so they appear
            // while the answer is still typing.
            setMessages(m => m.map((msg, j) => (j === index ? {
              ...msg,
              sources: meta.sources || [],
              answeredBy: meta.answered_by,
              chitchatKind: meta.chitchat_kind,
              rewrittenQuery: meta.rewritten_query
            } : msg)));
          },
          onToken: (_t, whole) => { pending = whole; },
          onDone: (d) => {
            flush();
            setMessages(m => m.map((msg, j) => (j === index ? { ...msg, duration_ms: d.duration_ms, streaming: false } : msg)));
          }
        });
      } finally {
        clearInterval(ticker);
        flush();
      }
    } catch (e) {
      // Replace the streaming placeholder if it is still empty, rather than
      // leaving a blank bubble above an error.
      setMessages(m => {
        const next = [...m];
        const last = next[next.length - 1];
        const errorMsg = {
          role: 'assistant',
          text: e?.message?.includes('unavailable')
            ? e.message
            : 'Sorry, I encountered an error processing your question. Make sure Ollama is running and records have been indexed.',
          sources: [],
          isError: true
        };
        if (last && last.role === 'assistant' && last.streaming && !last.text) next[next.length - 1] = errorMsg;
        else next.push(errorMsg);
        return next;
      });
      toast.error('RAG query failed');
    } finally {
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const handleIndexAll = async () => {
    setIndexing(true);
    try {
      const { data } = await rag.ingestAll();
      // remaining_after matters: the endpoint works in batches of 50, and it now
      // also repairs records embedded under a previous convention, so one click
      // is often not enough. Saying so beats letting the user assume it finished.
      if (data.remaining_after > 0) {
        toast.success(
          `Indexing ${data.queued} records in background — ${data.remaining_after} still to go, click again when this batch finishes`,
          { duration: 7000 }
        );
      } else if (data.queued === 0) {
        toast.success('Everything is already indexed');
      } else {
        toast.success(`Indexing ${data.queued} records in background`);
      }
      setTimeout(() => rag.status().then(r => setStatus(r.data)), 3000);
    } catch (e) {
      toast.error('Indexing failed');
    } finally {
      setIndexing(false);
    }
  };

  const ollamaOk = status?.ollama?.ok;

  return (
    <div className="flex flex-col h-full max-h-screen">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100 bg-white flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-blue-500 flex items-center justify-center">
            <Sparkles size={14} className="text-white" />
          </div>
          <div>
            <h1 className="text-sm font-semibold text-gray-900">Ask {cityName && cityName !== 'Generic' ? `${cityName} ` : ''}records</h1>
            <p className="text-xs text-gray-400">
              {status?.ollama?.embed_model && status?.ollama?.chat_model
                ? `Semantic search powered by ${status.ollama.embed_model} + ${status.ollama.chat_model}`
                : 'Semantic search powered by on-premises AI'}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          {/* Threads. Only shown once there is history to show — an empty
              dropdown on a fresh install would just be a dead button. */}
          {threads.length > 0 && (
            <div className="relative">
              <button onClick={() => setShowThreads(v => !v)}
                className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors">
                <Clock size={12} />
                History
              </button>
              {showThreads && (
                <>
                  {/* Click-away layer: a dropdown with no way to dismiss it
                      except selecting something is a trap. */}
                  <div className="fixed inset-0 z-10" onClick={() => setShowThreads(false)} />
                  <div className="absolute right-0 mt-1.5 w-80 max-h-96 overflow-y-auto bg-white rounded-xl border border-gray-200 shadow-lg z-20 py-1">
                    {threads.map(t => (
                      <div key={t.id} onClick={() => openThread(t.id)}
                        className={`group flex items-start gap-2 px-3 py-2 cursor-pointer hover:bg-gray-50 transition-colors
                          ${t.id === conversationId ? 'bg-blue-50/60' : ''}`}>
                        <MessageSquare size={12} className="text-gray-300 mt-0.5 flex-shrink-0" />
                        <div className="min-w-0 flex-1">
                          {renamingId === t.id ? (
                            /* Clicks and keys are stopped from reaching the row,
                               which would otherwise open the thread mid-edit.
                               Enter commits, Escape abandons, blur commits so the
                               edit is not silently lost by clicking away. */
                            <input autoFocus value={renameText}
                              onClick={e => e.stopPropagation()}
                              onChange={e => setRenameText(e.target.value)}
                              onBlur={() => commitRename(t.id)}
                              onKeyDown={e => {
                                e.stopPropagation();
                                if (e.key === 'Enter') { e.preventDefault(); commitRename(t.id); }
                                if (e.key === 'Escape') { e.preventDefault(); setRenamingId(null); }
                              }}
                              className="w-full text-xs px-1.5 py-0.5 rounded border border-blue-300 focus:outline-none focus:border-blue-500" />
                          ) : (
                            <div className="text-xs text-gray-800 truncate">{t.title || 'Untitled'}</div>
                          )}
                          <div className="text-xs text-gray-400 mt-0.5">
                            {t.message_count} message{t.message_count === 1 ? '' : 's'}
                            {t.updated_at ? ` · ${format(new Date(t.updated_at), 'MMM d, HH:mm')}` : ''}
                          </div>
                        </div>
                        <button onClick={(e) => startRename(t, e)}
                          title="Rename conversation"
                          className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-blue-500 transition-all flex-shrink-0">
                          <Pencil size={12} />
                        </button>
                        <button onClick={(e) => deleteThread(t.id, e)}
                          title="Delete conversation"
                          className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-500 transition-all flex-shrink-0">
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* New chat — clears the thread so the next question starts fresh
              rather than resolving against an unrelated conversation. */}
          <button onClick={newThread} disabled={loading}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors">
            <Plus size={12} />
            New chat
          </button>

          {/* Ollama status */}
          <div className={`flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border
            ${ollamaOk ? 'bg-green-50 text-green-700 border-green-200' : 'bg-amber-50 text-amber-700 border-amber-200'}`}>
            {ollamaOk
              ? <><CheckCircle size={11} /> Ollama ready</>
              : <><AlertCircle size={11} /> Ollama offline</>}
          </div>

          {/* Index status */}
          {status && (
            <div className="text-xs text-gray-400">
              {status.indexed_records}/{status.indexable_records} records indexed
            </div>
          )}

          <button onClick={handleIndexAll} disabled={indexing || !ollamaOk}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 transition-colors">
            <Database size={12} className={indexing ? 'animate-pulse' : ''} />
            {indexing ? 'Indexing...' : 'Index all'}
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-6 py-4 space-y-5">
        {messages.length === 0 && (
          <div className="max-w-2xl mx-auto pt-8">
            <div className="text-center mb-8">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-100 to-blue-100 flex items-center justify-center mx-auto mb-3">
                <Sparkles size={20} className="text-purple-500" />
              </div>
              <h2 className="text-base font-medium text-gray-800">Ask anything about {cityName && cityName !== 'Generic' ? `${cityName} ` : 'city '}records</h2>
              <p className="text-sm text-gray-400 mt-1">Semantic search finds relevant content even if you don't know the exact words</p>
            </div>
            <div className="grid grid-cols-1 gap-2">
              {exampleQuestions.map((q, i) => (
                <button key={i} onClick={() => ask(q)} disabled={loading || !ollamaOk}
                  className="text-left px-4 py-3 bg-white rounded-xl border border-gray-100 text-sm text-gray-600 hover:border-blue-200 hover:text-blue-700 hover:bg-blue-50/30 transition-all disabled:opacity-40">
                  "{q}"
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'} max-w-4xl mx-auto w-full`}>
            {msg.role === 'user' ? (
              <div className="bg-blue-600 text-white rounded-2xl rounded-tr-sm px-4 py-3 max-w-lg text-sm">
                {msg.text}
              </div>
            ) : (
              <div className="flex-1 min-w-0 max-w-2xl" style={msg.streaming && !msg.text ? { display: 'none' } : undefined}>
                <div className={`rounded-2xl rounded-tl-sm px-4 py-3 text-sm leading-relaxed
                  ${msg.isError ? 'bg-red-50 text-red-700 border border-red-100' : 'bg-white border border-gray-100 text-gray-800'}`}>
                  <div>{msg.isError ? msg.text : <>
                    <ReactMarkdown remarkPlugins={[remarkGfm]} components={{
                    p: ({children}) => <p className="mb-2 last:mb-0">{children}</p>,
                    strong: ({children}) => <strong className="font-semibold text-gray-900">{children}</strong>,
                    ul: ({children}) => <ul className="list-disc pl-5 mb-2 space-y-1">{children}</ul>,
                    ol: ({children}) => <ol className="list-decimal pl-5 mb-2 space-y-1">{children}</ol>,
                    li: ({children}) => <li>{children}</li>,
                    code: ({children}) => <code className="px-1 py-0.5 rounded bg-gray-100 text-[0.85em] font-mono">{children}</code>,
                    // Tables need remark-gfm to exist at all — base react-markdown
                    // implements CommonMark, which has no table syntax, so before
                    // this a pipe table rendered as a paragraph of pipes.
                    // The wrapper scrolls: this bubble is max-w-2xl and a 7-column
                    // financial table would otherwise be clipped with no way to
                    // reach the right-hand columns (the same defect Finding #61
                    // describes on Smart Query).
                    table: ({children}) => (
                      <div className="my-2 overflow-x-auto rounded-lg border border-gray-200">
                        <table className="min-w-full text-xs border-collapse">{children}</table>
                      </div>
                    ),
                    thead: ({children}) => <thead className="bg-gray-50">{children}</thead>,
                    tbody: ({children}) => <tbody>{children}</tbody>,
                    tr: ({children}) => <tr className="border-b border-gray-100 last:border-0">{children}</tr>,
                    th: ({children}) => <th className="px-3 py-2 text-left font-semibold text-gray-700 whitespace-nowrap">{children}</th>,
                    td: ({children}) => <td className="px-3 py-2 text-gray-700 whitespace-nowrap align-top">{children}</td>,
                    // remark-gfm also turns bare URLs into links. Answers are
                    // generated from OCR'd documents, so a URL in a scanned page
                    // can end up here — keep it visible but never a bare target
                    // for a click-through.
                    a: ({href, children}) => (
                      <a href={href} target="_blank" rel="noopener noreferrer nofollow"
                        className="text-blue-600 underline decoration-blue-200 hover:decoration-blue-500">{children}</a>
                    ),
                  }}>{msg.text}</ReactMarkdown>
                    {msg.streaming && (
                      <span className="inline-block w-1.5 h-3.5 ml-0.5 align-middle bg-purple-400 animate-pulse rounded-sm" />
                    )}
                  </>}</div>
                  {(msg.duration_ms || msg.answeredBy || voiceEnabled) && (
                    <div className="text-xs text-gray-300 mt-2 flex items-center gap-2 flex-wrap">
                      {msg.duration_ms && <span>{(msg.duration_ms / 1000).toFixed(1)}s</span>}
                      {/* 'Counted from the catalogue' vs 'From documents' — a
                          misrouted question otherwise just looks like a wrong
                          answer, with nothing to say why. */}
                      {msg.answeredBy === 'catalog' && (
                        <span className="text-indigo-400">· counted from the record catalogue</span>
                      )}
                      {/* Says plainly that no search happened. Without it, a
                          greeting looks identical to a search that found nothing,
                          and an out-of-scope decline looks like a failure. */}
                      {(msg.answeredBy === 'chitchat' || msg.answeredBy === 'clarify') && (
                        <span className="text-gray-400">· no records searched</span>
                      )}
                      {/* Read aloud. Local browser voices only — no audio and no
                          answer text leaves the device (Finding #108 explains why
                          that is worth saying for this half and not the other). */}
                      {voiceEnabled && !msg.isError && msg.text && (
                        <button onClick={() => toggleSpeak(i, msg.text)}
                          title={speakingIndex === i ? 'Stop reading' : 'Read this answer aloud'}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border font-medium transition-colors ${
                            speakingIndex === i
                              ? 'bg-blue-50 text-blue-700 border-blue-200'
                              : 'bg-white text-blue-600 border-blue-200 hover:bg-blue-50'}`}>
                          {speakingIndex === i ? <Square size={10} /> : <Volume2 size={11} />}
                          {speakingIndex === i ? 'Stop' : 'Listen'}
                        </button>
                      )}
                      {msg.rewrittenQuery && (
                        <span className="text-gray-300" title={`Searched for: ${msg.rewrittenQuery}`}>
                          · searched for "{msg.rewrittenQuery.length > 50 ? msg.rewrittenQuery.slice(0, 50) + '…' : msg.rewrittenQuery}"
                        </span>
                      )}
                    </div>
                  )}
                </div>
                {msg.sources?.length > 0 && (
                  <div className="mt-2">
                    <SourceList sources={msg.sources} apiBase={apiBase} />
                  </div>
                )}
              </div>
            )}
          </div>
        ))}

        {/* Only while nothing has arrived yet. Once the first token lands the
            answer bubble itself is the progress indicator, and showing both at
            once would read as two things happening. */}
        {loading && !messages.some(m => m.streaming && m.text) && (
          <div className="flex justify-start max-w-4xl mx-auto w-full">
            <div className="bg-white border border-gray-100 rounded-2xl rounded-tl-sm px-4 py-3 flex items-center gap-2">
              <RefreshCw size={14} className="animate-spin text-purple-400" />
              <span className="text-sm text-gray-400">Searching records...</span>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="px-6 py-4 border-t border-gray-100 bg-white flex-shrink-0">
        <div className="max-w-4xl mx-auto flex gap-3">
          <input ref={inputRef}
            value={question}
            onChange={e => {
              // Typing ENDS dictation rather than competing with it. The next
              // interim result would otherwise overwrite the correction that was
              // just made by hand, and the two would fight on every keystroke.
              if (dictating) dictation.stop();
              setQuestion(e.target.value);
            }}
            onKeyDown={e => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                // Block the SEND while an answer is in flight, never the typing.
                // The input used to be disabled outright, so you could not compose
                // your next question during the 8-24s wait (todo #176). Nothing is
                // auto-sent: the user presses Enter themselves when ready.
                if (!loading && ollamaOk) ask();
              }
            }}
            placeholder={dictating
              ? "Listening — speak now, then edit before sending"
              : ollamaOk
                ? (loading ? "Type your next question — press Enter when the answer arrives" : "Ask a question about any city record...")
                : "Start Ollama to enable semantic search"}
            className="flex-1 px-4 py-3 rounded-xl border border-gray-200 text-sm focus:outline-none focus:border-purple-400 focus:ring-2 focus:ring-purple-50 disabled:bg-gray-50 disabled:text-gray-400" />
          {/* Dictation. Rendered only where the browser can transcribe LOCALLY —
              an absent button is correct behaviour on Edge and Safari, not a
              degradation, and there is deliberately no cloud fallback to offer
              instead. Never triggers a send: it only fills the box. */}
          {dictationOffered && (
            <button onClick={toggleDictation}
              title={micBlocked
                ? 'Microphone blocked — allow it in your browser settings'
                : preparingMic
                  ? 'Downloading the on-device voice pack — click to cancel'
                  : dictating
                    ? 'Stop dictating (or release Ctrl+Space)'
                    : dictationReady === 'available'
                      ? 'Dictate your question — or hold Ctrl+Space'
                      : 'Set up dictation (downloads the on-device voice pack once)'}
              className={`w-11 h-11 rounded-xl border flex items-center justify-center transition-all flex-shrink-0 disabled:opacity-40
                ${dictating
                  ? 'bg-red-50 border-red-200 text-red-600 animate-pulse'
                  : micBlocked
                    ? 'bg-gray-50 border-gray-200 text-gray-300'
                    : 'bg-white border-gray-200 text-gray-500 hover:border-purple-300 hover:text-purple-600'}`}>
              {preparingMic
                ? <Loader2 size={15} className="animate-spin" />
                : micBlocked ? <MicOff size={15} /> : <Mic size={15} />}
            </button>
          )}
          <button onClick={() => ask()} disabled={loading || !question.trim() || !ollamaOk}
            className="w-11 h-11 bg-gradient-to-br from-purple-600 to-blue-600 text-white rounded-xl flex items-center justify-center hover:opacity-90 transition-all disabled:opacity-40 disabled:cursor-not-allowed flex-shrink-0">
            <Send size={15} />
          </button>
        </div>
        <div className="max-w-4xl mx-auto mt-1.5">
          {/* A live microphone must be unmistakable — an accessibility obligation
              as much as a UX one. It also states WHERE the audio goes, at the
              moment the question is being asked rather than in a settings page. */}
          {dictating ? (
            <p className="text-xs text-red-500 text-center">
              ● Listening — on-device only, no audio leaves this computer · nothing is sent until you press Enter
            </p>
          ) : preparingMic ? (
            /* A mute spinner is indistinguishable from a hang. Say what is being
               waited for and that it happens once. */
            <p className="text-xs text-purple-500 text-center">
              Downloading the on-device voice pack — one time only · click the mic to cancel
            </p>
          ) : dictationReady === 'downloading' ? (
            <p className="text-xs text-gray-400 text-center">
              Preparing on-device dictation in the background — the mic will be ready shortly
            </p>
          ) : (
            <p className="text-xs text-gray-300 text-center">
              Answers are generated from indexed records only · Always verify against source documents
              {dictationOffered && dictationReady === 'available' && ' · Hold Ctrl+Space to dictate'}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
